// src/signup/ConfirmScreen.tsx: /signup/confirm?t=<token>, the page the sign-up email's button opens (docs/DECISIONS.md
// "Builder sign-up (2026-10-20)"; docs/SPEC.md 17). It renders on app.homebuild.ai, where there is no builder, so
// src/main.tsx shows it before the normal boot and it uses no tenant. It is the next step of the page the builder signed up
// on (homebuild.ai/start, owner 2026-10-26): the same frame (src/platform/Shell.tsx), the same navy side (the wordmark, "Set
// up homebuild.", the one line, the four ticks), and the page's words in the middle of the white side. No card.
// Mail scanners open links, so opening the page only peeks (GET /api/platform/signup/peek); the one button's POST makes
// the builder and sends the person to its address, signed in. An expired link offers the sign-up form again (this
// environment's own, from the 410's `formOrigin`), a link already used offers the builder's sign-in page.
import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/api";
import { ApiError } from "../lib/http";
import { Shell } from "../platform/Shell";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Spinner } from "../ui/Feedback";
import "../styles/confirm.css";

/** Where someone whose link expired starts again when the Worker names no form of its own. */
export const SIGNUP_URL = "https://homebuild.ai/start/";

/** The sign-up form of the environment that sent the link: its own origin (staging's site on staging), else homebuild.ai. */
export function signupUrl(formOrigin?: string | null): string {
  if (!formOrigin) return SIGNUP_URL;
  try {
    const u = new URL(formOrigin);
    return u.protocol === "https:" || u.protocol === "http:" ? `${u.origin}/start/` : SIGNUP_URL;
  } catch {
    return SIGNUP_URL;
  }
}

type View =
  | { kind: "loading" }
  | { kind: "ready"; company: string; email: string }
  | { kind: "used"; signinUrl: string }
  | { kind: "expired"; formOrigin: string | null }
  | { kind: "error" };

/** The token in the address (`?t=`), or null. */
export function tokenFromSearch(search: string): string | null {
  const t = new URLSearchParams(search).get("t");
  return t && t.length <= 200 ? t : null;
}

const expiredView = (e: ApiError): View => ({ kind: "expired", formOrigin: e.fields?.formOrigin ?? null });

/** What they get: /start's four ticks, word for word. */
const TICKS = ["Your homes, pulled from your website", "Your inquiry form, in your colors", "Your team, invited by email", "Free until you open to families"];

/** /start's navy side, so the link lands on the page they signed up on. */
function SignupSide() {
  return (
    <>
      <div class="cf-side-head">
        <p class="cf-side-h">Set up homebuild.</p>
      </div>
      <ul class="qt-checks cf-ticks">
        {TICKS.map((t) => (
          <li key={t}>
            <Icon name="check" size="sm" />
            {t}
          </li>
        ))}
      </ul>
    </>
  );
}

function Title(props: { first: string; second?: string }) {
  return (
    <h1 class="cf-title">
      {props.first}
      {props.second ? <em>{props.second}</em> : null}
    </h1>
  );
}

/** A short state's words beside the side with the wordmark only, like the quote page's. */
function Short(props: { children: preact.ComponentChildren }) {
  return (
    <Shell screen="signup-confirm" mid>
      <div class="qt-s-body">{props.children}</div>
    </Shell>
  );
}

export function ConfirmScreen(props: { token?: string | null }) {
  const token = props.token === undefined ? tokenFromSearch(window.location.search) : props.token;
  const [view, setView] = useState<View>(token ? { kind: "loading" } : { kind: "error" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    const ctl = new AbortController();
    api
      .signupPeek(token, { signal: ctl.signal })
      .then((p) => setView(p.signinUrl ? { kind: "used", signinUrl: p.signinUrl } : { kind: "ready", company: p.company, email: p.email }))
      .catch((e: unknown) => {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setView(e instanceof ApiError && e.code === "expired_or_used" ? expiredView(e) : { kind: "error" });
      });
    return () => ctl.abort();
  }, [token]);

  const confirm = async () => {
    if (!token) return;
    setBusy(true);
    setProblem(null);
    try {
      const { next } = await api.signupConfirm(token);
      window.location.assign(next);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.code === "expired_or_used") setView(expiredView(e));
      else setProblem(e instanceof ApiError && e.code === "network" ? e.message : "That didn't go through. Try again.");
    }
  };

  switch (view.kind) {
    case "loading":
      return (
        <Short>
          <div class="cf-wait">
            <Spinner label="Opening your link" />
          </div>
        </Short>
      );
    case "ready":
      return (
        <Shell screen="signup-confirm" label="Setting up homebuild" side={<SignupSide />} mid>
          <div class="qt-s-body cf-ready">
            <Title first={`Set up ${view.company}.`} />
            <p class="cf-as">
              You&rsquo;ll sign in as <b>{view.email}</b>.
            </p>
            <Button size="lg" class="cf-go" busy={busy} onClick={() => void confirm()}>
              Confirm and Start Setup
            </Button>
            {problem ? (
              <p class="cf-problem" role="alert">
                {problem}
              </p>
            ) : null}
          </div>
        </Shell>
      );
    case "used":
      return (
        <Short>
          <Title first="You're already set up." />
          <Button size="lg" class="cf-go" href={view.signinUrl}>
            Sign In
          </Button>
        </Short>
      );
    case "expired":
      return (
        <Short>
          <Title first="This link has expired." second="Links last 24 hours." />
          <Button size="lg" class="cf-go" href={signupUrl(view.formOrigin)}>
            Sign Up Again
          </Button>
        </Short>
      );
    case "error":
      return (
        <Short>
          <Title first="We couldn't load this page." />
          <Button size="lg" class="cf-go" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Short>
      );
  }
}
